import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from '../../../shared/material.module';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { ContentService } from '../content.service';

export interface CoursePlanLecture {
  title: string;
  subject?: string;
}

export interface CoursePlanSection {
  title: string;
  lectures: CoursePlanLecture[];
  isExpanded?: boolean; // For UI toggle
}

export interface CoursePlan {
  sections: CoursePlanSection[];
  standaloneLectures: CoursePlanLecture[];
}

@Component({
  selector: 'app-create-knowledge',
  templateUrl: './create-knowledge.component.html',
  styleUrls: ['./create-knowledge.component.scss'],
  standalone: true,
  imports: [MaterialModule, TranslateModule, FormsModule, DragDropModule, MatIconModule, MatButtonModule, MatInputModule, MatProgressSpinnerModule, MatTooltipModule],
})
export class CreateKnowledgeComponent {
  courseName = '';
  courseDescription = '';
  instructions = '';

  loading = false;
  isGenerating = false;
  buttonLock = false;

  showTOC = false;
  toc: CoursePlan = {
    sections: [],
    standaloneLectures: [],
  };

  constructor(
    public dialogRef: MatDialogRef<CreateKnowledgeComponent>,
    private contentService: ContentService,
    private toast: ToastService,
    private translate: TranslateService,
    private errorHandler: ErrorHandlerService
  ) {}

  generateTOC() {
    if (!this.courseName) {
      this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('Global.TitleMissing') }); // Assuming keys exists or using fallback
      return;
    }

    this.isGenerating = true;
    this.contentService
      .generateCoursePlan({
        subject: this.courseName,
        description: this.courseDescription,
        language: this.translate.currentLang || 'fr',
        instructions: this.instructions,
      })
      .subscribe({
        next: (plan: CoursePlan) => {
          this.toc = {
            sections: plan.sections.map((s) => ({ ...s, isExpanded: true })),
            standaloneLectures: plan.standaloneLectures || [],
          };
          this.showTOC = true;
          this.isGenerating = false;
        },
        error: (err) => {
          console.error(err);
          this.isGenerating = false;
          this.toast.showWithoutHeader({ level: 'error', text: 'Failed to generate plan' });
        },
      });
  }

  regenerateTOC() {
    this.generateTOC();
  }

  createCourse() {
    this.loading = true;
    this.buttonLock = true;

    const payload: any = {
      name: this.courseName,
      description: this.courseDescription,
      language: this.translate.currentLang || 'fr',
    };

    if (this.showTOC) {
      payload.plan = {
        sections: this.toc.sections.map((s) => ({
          title: s.title,
          lectures: s.lectures.map((l) => ({ title: l.title, subject: l.subject })),
        })),
        standaloneLectures: this.toc.standaloneLectures.map((l) => ({ title: l.title, subject: l.subject })),
      };
    }

    this.contentService.createCourse(payload).subscribe({
      next: () => {
        this.toast.showWithoutHeader({ level: 'success', text: this.translate.instant('Snackbar.KnowledgeCreated') });
        this.dialogRef.close('ok');
      },
      error: (err) => {
        this.loading = false;
        this.buttonLock = false;
        if (err.error && err.error.msg) {
          if (err.error && err.error.msg.msg === 'alreadyExists') {
            this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('Snackbar.CourseAlreadyExistMsg') });
          }
        } else if (!this.errorHandler.handle401Error(err)) {
          this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('Snackbar.CreatingCourseFail') });
        }
      },
    });
  }

  // --- UI Actions ---

  get connectedDropLists(): string[] {
    // IDs for all section lists + standalone list
    const lists = this.toc.sections.map((_, i) => `section-${i}`);
    lists.push('standalone-lectures');
    return lists;
  }

  toggleSection(section: CoursePlanSection) {
    section.isExpanded = !section.isExpanded;
  }

  addSection() {
    this.toc.sections.push({ title: 'New Section', lectures: [], isExpanded: true });
  }

  deleteSection(index: number) {
    this.toc.sections.splice(index, 1);
  }

  addLectureToSection(sectionIndex: number) {
    this.toc.sections[sectionIndex].lectures.push({ title: 'New Lecture' });
  }

  addStandaloneLecture() {
    this.toc.standaloneLectures.push({ title: 'New Lecture' });
  }

  deleteStandaloneLecture(index: number) {
    this.toc.standaloneLectures.splice(index, 1);
  }

  deleteLectureFromSection(sectionIndex: number, lectureIndex: number) {
    this.toc.sections[sectionIndex].lectures.splice(lectureIndex, 1);
  }

  // --- Drag & Drop ---

  dropSection(event: CdkDragDrop<CoursePlanSection[]>) {
    moveItemInArray(this.toc.sections, event.previousIndex, event.currentIndex);
  }

  dropLecture(event: CdkDragDrop<CoursePlanLecture[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }
}
